<template>
  <div class="bg-white rounded-3xl max-md:rounded-2xl overflow-hidden relative">
    <!-- Обложка: обложка канала или сильно размытый аватар как заглушка (как на витрине) -->
    <div class="h-[158px] max-md:h-[92px] w-full relative overflow-hidden bg-bgnew">
      <NuxtImg
        v-if="place?.cover"
        :src="place.cover"
        alt="Обложка канала"
        class="w-full h-full object-cover"
      />
      <template v-else-if="place?.avatar">
        <img :src="place.avatar" alt="" class="w-full h-full object-cover blur-2xl scale-125" />
        <div class="avatar-grain absolute inset-0 pointer-events-none"></div>
      </template>
    </div>

    <!-- Аватар -->
    <div
      class="absolute left-8 top-[118px] size-20 max-md:left-3 max-md:top-[73px] max-md:size-[60px] rounded-full border-2 border-white bg-bgnew overflow-hidden shadow-[0px_3px_12px_0px_rgba(49,118,255,0.14)] z-[1]"
    >
      <NuxtImg
        v-if="place?.avatar"
        :src="place.avatar"
        alt="Аватар канала"
        class="w-full h-full object-cover"
      />
    </div>

    <div class="px-8 pb-6 max-md:px-3 max-md:pb-4">
      <!-- Бейджи РКН и соцсети (справа сверху, на уровне свисающего аватара) -->
      <div class="flex items-center justify-end gap-2 min-h-[40px] max-md:min-h-[28px] pt-3">
        <div
          v-if="place?.rknRegistration?.isRegistered"
          class="bg-[#2DCD80] rounded-[20px] flex items-center gap-1 h-[22px] px-1.5 w-fit"
        >
          <svg viewBox="0 0 16 16" fill="none" class="size-3.5 shrink-0">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z"
              fill="white"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M11.8634 5.12225C12.1474 5.40627 12.1474 5.86675 11.8634 6.15077L7.34913 10.878C7.06511 11.1621 6.60463 11.1621 6.32061 10.878L4.13879 8.69622C3.85478 8.41221 3.85478 7.95172 4.13879 7.6677C4.42281 7.38369 4.8833 7.38369 5.16731 7.6677L6.83487 9.33526L10.8349 5.12225C11.1189 4.83823 11.5794 4.83823 11.8634 5.12225Z"
              fill="#2DCD80"
            />
          </svg>
          <span class="text-white text-xs font-semibold leading-4 whitespace-nowrap">В реестре РКН</span>
        </div>
        <ChannelSocialBadge :place="place" />
      </div>

      <!-- Название и описание -->
      <!-- Ссылка на канал зашита прямо в название: отдельной кнопки перехода
           в новом виде нет. text-txprimary обязателен — глобальное правило
           красит любую ссылку в primary -->
      <h1 class="text-txprimary font-bold text-[32px] leading-[1.25] max-md:text-lg mt-2 max-md:mt-1">
        <NuxtLink
          v-if="place?.url"
          :to="place.url"
          target="_blank"
          rel="nofollow noindex noopener noreferrer"
          class="text-txprimary hover:underline"
        >
          {{ place?.name || "" }}
        </NuxtLink>
        <template v-else>{{ place?.name || "" }}</template>
      </h1>
      <p
        v-if="place?.description"
        class="text-base max-md:text-xs text-txprimary/60 leading-normal mt-2 max-w-[906px]"
      >
        {{ displayedDescription }}<span
          v-if="isDescriptionTruncatable"
          @click="isDescriptionExpanded = !isDescriptionExpanded"
          class="cursor-pointer ml-1"
          :style="{ color: accentColor }"
        >{{ isDescriptionExpanded ? "Скрыть" : "Подробнее" }}</span>
      </p>

      <!-- Статистика: десктоп — пилюли (значение + подпись в строку),
           мобильный — три равные карточки (подпись сверху, значение снизу) -->
      <div class="mt-5 max-md:mt-4 flex flex-wrap gap-2 max-md:flex-nowrap max-md:gap-2">
        <div
          v-for="stat in stats"
          :key="stat.label"
          class="bg-bgnew rounded-3xl max-md:rounded-2xl px-[21px] py-[9px] max-md:px-3 max-md:py-2 flex items-center gap-4 max-md:flex-1 max-md:min-w-0 max-md:flex-col-reverse max-md:items-start max-md:gap-2"
        >
          <span class="text-txprimary font-bold text-base leading-normal">
            {{ stat.value }}
          </span>
          <span class="text-sm max-md:text-xs text-txprimary/60 leading-tight">
            {{ stat.label }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { FullPlace } from "~/interfaces/publisher";
  import { usePlaceModuleStore } from "~/store/PlaceModule";

  const props = withDefaults(defineProps<{ place?: FullPlace | null }>(), { place: null });

  const placeStore = usePlaceModuleStore();
  const { colorOptions } = storeToRefs(placeStore);
  const accentColor = computed(() =>
    props.place?.buttonsColor ? (colorOptions.value[props.place.buttonsColor] ?? "#3176FF") : "#3176FF"
  );

  const DESCRIPTION_MAX_LENGTH = 200;
  const isDescriptionExpanded = ref(false);
  const isDescriptionTruncatable = computed(
    () => (props.place?.description?.length || 0) > DESCRIPTION_MAX_LENGTH
  );
  const displayedDescription = computed(() => {
    const desc = props.place?.description || "";
    if (!isDescriptionTruncatable.value || isDescriptionExpanded.value) return desc;
    return desc.slice(0, DESCRIPTION_MAX_LENGTH).trimEnd() + "...";
  });
  watch(
    () => props.place?.description,
    () => {
      isDescriptionExpanded.value = false;
    }
  );

  const stats = computed(() => [
    {
      value: (props.place?.subscribersStatistic?.count || 0).toLocaleString(),
      label: "Подписчики",
    },
    {
      value: (props.place?.viewsStatistic?.countLast24h || 0).toLocaleString(),
      label: "Просмотры",
    },
    { value: `${props.place?.er || 0}%`, label: "ERR" },
  ]);
</script>
